import { Component, Input, OnInit, inject } from "@angular/core";
import { SubDivisionStore } from "@app/services/store/roster/sub-division-store";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ShiftStore } from "@app/services/store/roster/shift-store";
import { MatDialog } from "@angular/material/dialog";
import { Roster } from "@app/models/rest/roster/roster";
import { SubDivision } from "@app/models/rest/roster/sub-division";
import { ResourceCollection } from "@lagoshny/ngx-hateoas-client";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { Store as NgrxStore } from "@ngrx/store";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { RosterAssignmentStore } from "@app/services/store/roster/roster-assignment-store";
import { AddAssignmentDialogComponent } from "@app/view/setup/resourcing/roster/add-assignment-dialog/add-assignment-dialog.component";
import { RosterAssignment } from "@app/models/rest/roster/roster-assignment";
import { Store } from "@bryntum/core-thin";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { HateoasResourceService } from "@lagoshny/ngx-hateoas-client";
import { CustomAjaxStore } from "@app/view/shared/base-pages/base-data-table-page/custom-ajax-store.component";
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardContent
} from "@angular/material/card";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { GridComponent } from "../../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";

@Component({
  selector: "app-assignment-edit",
  templateUrl: "./assignment-edit.component.html",
  styleUrls: ["./assignment-edit.component.css"],
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatButton,
    MatIcon,
    MatCardContent,
    GridComponent,
    MatPaginator
  ]
})
export class AssignmentEditComponent
  extends BaseDataTablePageDirective<RosterAssignment>
  implements OnInit
{
  rosterAssignmentStore: RosterAssignmentStore;
  protected store: NgrxStore<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serviceErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  protected subDivisionStore = inject(SubDivisionStore);
  protected shiftStore = inject(ShiftStore);
  private hateoasResourceService = inject(HateoasResourceService);
  private clientConfigService = inject(ClientConfigService);

  @Input()
  public roster: Roster;
  public subDivisions: SubDivision[] = [];
  public subDivisionListStore = new Store({
    data: []
  });
  public pageEvent: any;
  public pageSizeOptions = [5, 10, 25, 50];

  @Input()
  public columns: object[] = [
    {
      text: "Division",
      field: "subDivision",
      flex: 1,
      renderer: ({ record }) => {
        // Handle HATEOAS projection and various possible formats
        if (!record.subDivision) return "none";

        // Direct string value case
        if (typeof record.subDivision === "string") return record.subDivision;

        // HATEOAS projection object case
        if (record.subDivision.name) return record.subDivision.name;

        // URL case - extract ID
        if (
          record.subDivision._links &&
          record.subDivision._links.self &&
          record.subDivision._links.self.href
        ) {
          const url = record.subDivision._links.self.href;
          const id = url.split("/").pop().split("{")[0];
          return `Division ${id}`;
        }

        // Fall back to ID if available
        return record.subDivision.id
          ? `Division ${record.subDivision.id}`
          : "unknown";
      },
      editor: {
        type: "dropdown",
        store: this.subDivisionListStore,
        displayField: "name",
        valueField: null
      }
    },
    {
      width: 60,
      type: "template",
      field: "resource.name",
      text: "Resource",
      flex: 1,
      template: ({ record }) => {
        // Handle HATEOAS projection and get image URL with fallback
        const imageUrl =
          record.resource && record.resource.imageUrl
            ? record.resource.imageUrl
            : "assets/profile.png";
        const resourceName =
          record.resource && record.resource.name
            ? record.resource.name
            : "Unknown";

        // Fixed styling for perfect circle with proper dimensions
        return `<div style="display:flex; align-items:center;">
                  <div style="width:40px; height:40px; margin-right:10px; overflow:hidden; border-radius:50%; box-shadow:1px 1px 3px rgba(0,0,0,0.2);">
                    <img src="${imageUrl}" alt="Resource" style="width:100%; height:100%; object-fit:cover;"/>
                  </div>
                  <span>${resourceName}</span>
                </div>`;
      },
      editor: false
    },
    {
      field: "startDate",
      text: "Start",
      flex: 1,
      format: "DD/MM/YYYY",
      type: "date",
      editor: true
    },
    {
      field: "endDate",
      text: "End",
      flex: 1,
      format: "DD/MM/YYYY",
      type: "date",
      editor: true
    },
    {
      field: "lastRunDate",
      text: "Last Run Date",
      flex: 1,
      format: "DD/MM/YYYY",
      type: "date",
      editor: true
    }
  ];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const rosterAssignmentStore = inject(RosterAssignmentStore);
    const store = inject<NgrxStore<any>>(NgrxStore);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serviceErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      rosterAssignmentStore,
      store,
      dialog,
      "RosterAssignment",
      formBuilder,
      serviceErrorHandler,
      ngxUiLoaderService
    );
    this.rosterAssignmentStore = rosterAssignmentStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serviceErrorHandler = serviceErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
    const hateoasResourceService = this.hateoasResourceService;
    const clientConfigService = this.clientConfigService;

    // Set the required services on the CustomAjaxStore class
    CustomAjaxStore.setClientConfigService(clientConfigService);
    CustomAjaxStore.setHateoasResourceService(hateoasResourceService);
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    super.ngOnInit();

    // Set autoCommit to false before loading data
    this.gridStore.autoCommit = false;

    // Load all subdivisions for the roster
    this.roster
      .getRelatedCollection<ResourceCollection<SubDivision>>("subDivisions")
      .subscribe((divisions) => {
        this.subDivisions = divisions.resources;
        this.subDivisionListStore.add(divisions.resources);

        // After loading subdivisions, load all assignments for the roster
        if (this.roster) {
          this.gridStore.fixedFilters = [
            {
              property: "subDivision.roster.id",
              operator: "Equals",
              value: this.roster.id
            }
          ];

          // Pass projection directly in the load parameters
          this.gridStore.load({
            params: {
              projection: "detail"
            }
          });
        }
      });
  }

  openNewAssignmentDialog() {
    // Open dialog without preselecting a subdivision
    let dialogRef = this.dialog.open(AddAssignmentDialogComponent, {
      data: {
        roster: this.roster,
        subDivisions: this.subDivisions
      }
    });

    dialogRef.afterClosed().subscribe((value) => {
      if (value) {
        this.gridStore.load({});
      }
    });
  }
}
